/**
 * Componente contenedor principal del módulo Tracking
 * Main container component for Tracking module
 */

import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { useQueryParams } from '@shared/hooks/useQueryParams';
import { useToast } from '@shared/hooks/use-toast';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTrackingData } from '../../hooks/useTrackingData';
import { TrackingFilters } from '../../models/TrackingFilters';
import { ApiCallsSection, DashboardSection, HistorySection, UnmatchedSection } from '../display';
import { TrackingFiltersBar } from '../interaction';
import { TrackingSkeleton } from '../skeletons';

type SectionType = 'dashboard' | 'api_calls' | 'history' | 'unmatched';

export const Main = () => {
  const { t } = useTranslation('trackingModule');
  const { toast } = useToast();
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const lastErrorShown = useRef<string | null>(null);

  // Query params para sincronizar filtros
  const { params: queryParams, updateParams: updateQueryParams } = useQueryParams({
    section: { type: 'string', default: 'dashboard' },
    courier_id: { type: 'number', default: null },
    hours: { type: 'number', default: 24 },
    date_from: { type: 'string', default: '' },
    date_to: { type: 'string', default: '' },
    page: { type: 'number', default: 1 },
    per_page: { type: 'number', default: 50 },
  });

  // Estado de sección activa
  const [activeSection, setActiveSection] = useState<SectionType>((queryParams.section as SectionType) || 'dashboard');

  // Estados de filtros sincronizados con queryParams
  // Filtros independientes por sección
  const [dashboardFilters, setDashboardFilters] = useState<TrackingFilters>({
    courier_id: queryParams.courier_id ?? null,
    hours: queryParams.hours ?? 24,
  });
  const [apiCallsFilters, setApiCallsFilters] = useState<TrackingFilters>({});
  const [historyFilters, setHistoryFilters] = useState<TrackingFilters>({
    courier_id: queryParams.courier_id ?? null,
  });
  const [unmatchedFilters, setUnmatchedFilters] = useState<TrackingFilters>({
    courier_id: queryParams.courier_id ?? null,
    date_from: queryParams.date_from || undefined,
    date_to: queryParams.date_to || undefined,
  });

  // Obtener filtros según la sección activa
  const filters = useMemo(() => {
    switch (activeSection) {
      case 'dashboard':
        return dashboardFilters;
      case 'api_calls':
        return apiCallsFilters;
      case 'history':
        return historyFilters;
      case 'unmatched':
        return unmatchedFilters;
      default:
        return {};
    }
  }, [activeSection, dashboardFilters, apiCallsFilters, historyFilters, unmatchedFilters]);

  const [pagination, setPagination] = useState({
    page: queryParams.page ?? 1,
    per_page: queryParams.per_page ?? 50,
  });

  // Sincronizar estados con queryParams
  useEffect(() => {
    if (queryParams.section) {
      setActiveSection(queryParams.section as SectionType);
    }
  }, [queryParams.section]);

  // Inicializar filtros desde queryParams solo una vez
  useEffect(() => {
    if (queryParams.courier_id !== undefined) {
      setDashboardFilters((prev) => ({ ...prev, courier_id: queryParams.courier_id ?? null }));
      setHistoryFilters((prev) => ({ ...prev, courier_id: queryParams.courier_id ?? null }));
      setUnmatchedFilters((prev) => ({ ...prev, courier_id: queryParams.courier_id ?? null }));
    }
    if (queryParams.hours !== undefined) {
      setDashboardFilters((prev) => ({ ...prev, hours: queryParams.hours ?? 24 }));
    }
    if (queryParams.date_from !== undefined || queryParams.date_to !== undefined) {
      setUnmatchedFilters((prev) => ({
        ...prev,
        date_from: queryParams.date_from || undefined,
        date_to: queryParams.date_to || undefined,
      }));
    }
  }, []); // Solo al montar

  useEffect(() => {
    setPagination({
      page: queryParams.page ?? 1,
      per_page: queryParams.per_page ?? 50,
    });
  }, [queryParams.page, queryParams.per_page]);

  // Obtener datos
  const {
    filesComparison,
    webhooksComparison,
    apiComparison,
    courierStatus,
    outdatedOrders,
    historySummary,
    historyFailed,
    retryHistory,
    isRetryingHistory,
    unmatchedRecords,
  } = useTrackingData({ filters, pagination });

  // Detectar si hay algún error
  const hasError =
    filesComparison.error ||
    webhooksComparison.error ||
    apiComparison.error ||
    courierStatus.error ||
    outdatedOrders.error ||
    historySummary.error ||
    historyFailed.error ||
    unmatchedRecords.error;

  // Mostrar toast si hay error
  useEffect(() => {
    const errorMessages = [
      filesComparison.error,
      webhooksComparison.error,
      apiComparison.error,
      courierStatus.error,
      outdatedOrders.error,
      historySummary.error,
      historyFailed.error,
      unmatchedRecords.error,
    ]
      .filter(Boolean)
      .map((e: any) => e?.message || String(e));

    if (errorMessages.length > 0) {
      const errorMessage = errorMessages[0];
      if (errorMessage !== lastErrorShown.current) {
        lastErrorShown.current = errorMessage;
        toast({
          title: t('toasts.error.title'),
          description: errorMessage || t('toasts.error.unexpected'),
          variant: 'destructive',
        });
      }
    } else {
      lastErrorShown.current = null;
    }
  }, [
    filesComparison.error,
    webhooksComparison.error,
    apiComparison.error,
    courierStatus.error,
    outdatedOrders.error,
    historySummary.error,
    historyFailed.error,
    unmatchedRecords.error,
    toast,
    t,
  ]);

  // Marcar que la carga inicial terminó
  useEffect(() => {
    const isLoading =
      filesComparison.isLoading ||
      webhooksComparison.isLoading ||
      apiComparison.isLoading ||
      courierStatus.isLoading ||
      outdatedOrders.isLoading ||
      historySummary.isLoading ||
      historyFailed.isLoading ||
      unmatchedRecords.isLoading;

    if (!isLoading && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [
    filesComparison.isLoading,
    webhooksComparison.isLoading,
    apiComparison.isLoading,
    courierStatus.isLoading,
    outdatedOrders.isLoading,
    historySummary.isLoading,
    historyFailed.isLoading,
    unmatchedRecords.isLoading,
    isInitialLoad,
  ]);

  // Handler para cambio de sección
  const handleSectionChange = (section: SectionType) => {
    setActiveSection(section);
    updateQueryParams({ section, page: 1 });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  // Handler para cambio de filtros según la sección activa
  const handleFiltersChange = useCallback(
    (newFilters: TrackingFilters) => {
      switch (activeSection) {
        case 'dashboard':
          setDashboardFilters(newFilters);
          updateQueryParams({
            courier_id:
              newFilters.courier_id !== undefined && newFilters.courier_id !== null
                ? newFilters.courier_id
                : (null as any),
            hours: newFilters.hours ?? 24,
            page: 1,
          });
          break;
        case 'api_calls':
          setApiCallsFilters(newFilters);
          updateQueryParams({
            courier_id:
              newFilters.courier_id !== undefined && newFilters.courier_id !== null
                ? newFilters.courier_id
                : (null as any),
            page: 1,
          });
          break;
        case 'history':
          // Histórico no tiene filtros de fecha
          const historyFiltersClean = { ...newFilters };
          delete historyFiltersClean.date_from;
          delete historyFiltersClean.date_to;
          setHistoryFilters(historyFiltersClean);
          updateQueryParams({
            courier_id:
              historyFiltersClean.courier_id !== undefined && historyFiltersClean.courier_id !== null
                ? historyFiltersClean.courier_id
                : (null as any),
            page: 1,
          });
          break;
        case 'unmatched':
          setUnmatchedFilters(newFilters);
          updateQueryParams({
            courier_id:
              newFilters.courier_id !== undefined && newFilters.courier_id !== null
                ? newFilters.courier_id
                : (null as any),
            date_from: newFilters.date_from || '',
            date_to: newFilters.date_to || '',
            page: 1,
          });
          break;
      }
      setPagination((prev) => ({ ...prev, page: 1 }));
    },
    [activeSection, updateQueryParams],
  );

  // Handler para clic en courier (solo para api_calls)
  const handleCourierClick = useCallback(
    (courierId: number) => {
      if (activeSection === 'api_calls') {
        // Si ya está seleccionado, deseleccionar (limpiar filtro)
        const newCourierId = apiCallsFilters.courier_id === courierId ? null : courierId;
        handleFiltersChange({ courier_id: newCourierId });
      }
    },
    [activeSection, apiCallsFilters.courier_id, handleFiltersChange],
  );

  // Handler para reintentar histórico
  const handleRetryHistory = async (orderIds: number[]) => {
    try {
      await retryHistory({ order_ids: orderIds });
    } catch (error) {
      // El error ya se maneja en el hook
    }
  };

  // Mostrar skeleton solo en la carga inicial
  if (isInitialLoad) {
    return <TrackingSkeleton />;
  }

  const sections: { id: SectionType; label: string }[] = [
    { id: 'dashboard', label: t('sections.dashboard') },
    { id: 'api_calls', label: t('sections.api_calls') },
    { id: 'history', label: t('sections.history') },
    { id: 'unmatched', label: t('sections.unmatched') },
  ];

  return (
    <div className="px-6 py-4 space-y-6">
      {/* Tabs de navegación */}
      <div className="flex gap-1 border-b border-card-border mb-6">
        {sections.map((section) => (
          <button
            key={section.id}
            onClick={() => handleSectionChange(section.id)}
            className={`px-4 py-2 text-sm font-medium transition-colors relative ${
              activeSection === section.id ? 'text-primary' : 'text-subtext hover:text-text'
            }`}
          >
            {section.label}
            {activeSection === section.id && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />}
          </button>
        ))}
      </div>

      {/* Barra de filtros */}
      <TrackingFiltersBar filters={filters} onFiltersChange={handleFiltersChange} section={activeSection} />

      {/* Contenido de la sección activa */}
      {hasError && (
        <EmptyView
          type="emptyState"
          icon={EMPTY_VIEW_ICONS.ORDERS}
          title={t('empty.error.title')}
          description={t('empty.error.description')}
        />
      )}

      {!hasError && (
        <div className="space-y-6">
          {activeSection === 'dashboard' && (
            <DashboardSection
              filesComparison={filesComparison.data}
              webhooksComparison={webhooksComparison.data}
              apiComparison={apiComparison.data}
              isLoading={filesComparison.isLoading || webhooksComparison.isLoading || apiComparison.isLoading}
            />
          )}

          {activeSection === 'api_calls' && (
            <ApiCallsSection
              courierStatus={courierStatus.data}
              outdatedOrders={outdatedOrders.data}
              total={outdatedOrders.total}
              isLoading={courierStatus.isLoading || outdatedOrders.isLoading}
              isFetching={courierStatus.isFetching || outdatedOrders.isFetching}
              isFetchingNextPage={outdatedOrders.isFetchingNextPage}
              fetchNextPage={outdatedOrders.fetchNextPage}
              hasNextPage={outdatedOrders.hasNextPage}
              selectedCourierId={apiCallsFilters.courier_id ?? null}
              onCourierClick={handleCourierClick}
            />
          )}

          {activeSection === 'history' && (
            <HistorySection
              summary={historySummary.data}
              failed={historyFailed.data}
              total={historyFailed.total}
              isLoading={historySummary.isLoading || historyFailed.isLoading}
              isFetching={historySummary.isFetching || historyFailed.isFetching}
              isFetchingNextPage={historyFailed.isFetchingNextPage}
              fetchNextPage={historyFailed.fetchNextPage}
              hasNextPage={historyFailed.hasNextPage}
              onRetry={handleRetryHistory}
              isRetrying={isRetryingHistory}
            />
          )}

          {activeSection === 'unmatched' && (
            <UnmatchedSection
              unmatchedRecords={unmatchedRecords.data}
              total={unmatchedRecords.total}
              isLoading={unmatchedRecords.isLoading}
              isFetching={unmatchedRecords.isFetching}
              isFetchingNextPage={unmatchedRecords.isFetchingNextPage}
              fetchNextPage={unmatchedRecords.fetchNextPage}
              hasNextPage={unmatchedRecords.hasNextPage}
            />
          )}
        </div>
      )}
    </div>
  );
};
